import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { Colors, Spacing, FontSizes, BorderRadius } from '../constants/theme';

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 188, 242, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 188, 242, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 188, 242, 0);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const Container = styled.div`
  position: fixed;
  top: 80px;
  right: ${Spacing.xl}px;
  z-index: 900;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

const SyncCard = styled.div<{ status: 'syncing' | 'synced' | 'error' | 'offline' }>`
  background: ${Colors.surface};
  border: 1px solid ${props => {
    switch(props.status) {
      case 'syncing': return Colors.info;
      case 'synced': return Colors.success;
      case 'error': return Colors.danger;
      case 'offline': return Colors.warning;
      default: return Colors.border;
    }
  }};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.md}px ${Spacing.lg}px;
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  animation: ${slideIn} 0.3s ease-out;
  min-width: 250px;
`;

const StatusIcon = styled.div<{ status: 'syncing' | 'synced' | 'error' | 'offline' }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  position: relative;
  
  ${props => {
    switch(props.status) {
      case 'syncing':
        return css`
          background: ${Colors.info};
          animation: ${pulse} 2s infinite;
          
          &::after {
            content: '';
            position: absolute;
            top: -4px;
            left: -4px;
            right: -4px;
            bottom: -4px;
            border: 2px solid ${Colors.info};
            border-radius: 50%;
            border-top-color: transparent;
            animation: ${rotate} 1s linear infinite;
          }
        `;
      case 'synced':
        return css`
          background: ${Colors.success};
        `;
      case 'error':
        return css`
          background: ${Colors.danger};
        `;
      case 'offline':
        return css`
          background: ${Colors.warning};
        `;
      default:
        return css`
          background: ${Colors.textTertiary};
        `;
    }
  }}
`;

const SyncInfo = styled.div`
  flex: 1;
`;

const SyncTitle = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
  font-weight: 600;
`;

const SyncDetails = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  margin-top: 2px;
`;

const Timestamp = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
`;

const DataSourceIndicator = styled.div`
  background: ${Colors.background};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.sm}px;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  margin-top: ${Spacing.sm}px;
`;

const SourceIcon = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color};
`;

const SourceName = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${Colors.border};
  border-radius: 2px;
  overflow: hidden;
  margin-top: ${Spacing.xs}px;
`;

const ProgressFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: ${Colors.primary};
  transition: width 0.3s ease;
`;

interface SyncStatus {
  source: string;
  status: 'syncing' | 'synced' | 'error' | 'offline';
  lastSync?: Date;
  nextSync?: Date;
  recordsUpdated?: number;
  progress?: number;
  message?: string;
}

export const RealTimeSyncIndicator: React.FC = () => {
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);
  const [showIndicator, setShowIndicator] = useState(true);
  const location = useLocation();
  
  // Only show sync notifications on the data-sources page
  const isDataSourcesPage = location.pathname === '/data-sources';

  useEffect(() => {
    // Only run simulations on data-sources page
    if (!isDataSourcesPage) {
      setSyncStatuses([]);
      return;
    }
    
    // Simulate real-time sync updates
    const initialStatuses: SyncStatus[] = [
      {
        source: 'Workday',
        status: 'synced',
        lastSync: new Date(Date.now() - 5 * 60000),
        nextSync: new Date(Date.now() + 25 * 60000),
        recordsUpdated: 1847
      }
    ];
    setSyncStatuses(initialStatuses);

    // Simulate periodic sync cycles
    const syncInterval = setInterval(() => {
      setSyncStatuses(prev => {
        const statuses = [...prev];
        const randomSource = Math.floor(Math.random() * 3);
        const sources = ['Workday', 'SAP SuccessFactors', 'Excel Files'];
        
        // Check if source already exists
        const existingIndex = statuses.findIndex(s => s.source === sources[randomSource]);
        
        if (existingIndex >= 0) {
          // Update existing
          statuses[existingIndex] = {
            ...statuses[existingIndex],
            status: 'syncing',
            progress: 0
          };
        } else {
          // Add new sync
          statuses.push({
            source: sources[randomSource],
            status: 'syncing',
            progress: 0
          });
        }
        
        return statuses;
      });

      // Simulate progress
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 20;
        setSyncStatuses(prev => {
          const statuses = [...prev];
          const syncingIndex = statuses.findIndex(s => s.status === 'syncing');
          if (syncingIndex >= 0) {
            statuses[syncingIndex].progress = progress;
            
            if (progress >= 100) {
              statuses[syncingIndex] = {
                ...statuses[syncingIndex],
                status: 'synced',
                lastSync: new Date(),
                nextSync: new Date(Date.now() + 30 * 60000),
                recordsUpdated: Math.floor(Math.random() * 500) + 100,
                progress: undefined
              };
              clearInterval(progressInterval);
              
              // Auto-hide after 5 seconds
              setTimeout(() => {
                setSyncStatuses(prev => prev.filter(s => s.source !== statuses[syncingIndex].source));
              }, 5000);
            }
          }
          return statuses;
        });
      }, 500);

      return () => clearInterval(progressInterval);
    }, 15000); // Start new sync every 15 seconds

    return () => clearInterval(syncInterval);
  }, [isDataSourcesPage]);

  const formatTime = (date: Date): string => {
    const now = new Date();
    const diff = Math.abs(now.getTime() - date.getTime());
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 minute ago';
    if (minutes < 60) return `${minutes} minutes ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    
    return date.toLocaleDateString();
  };

  const getSourceColor = (source: string): string => {
    switch(source) {
      case 'Workday': return Colors.primary;
      case 'SAP SuccessFactors': return Colors.secondary;
      case 'Excel Files': return Colors.success;
      default: return Colors.textTertiary;
    }
  };

  // Don't render if not on data-sources page or no sync statuses
  if (!isDataSourcesPage || !showIndicator || syncStatuses.length === 0) return null;

  return (
    <Container>
      {syncStatuses.map((sync, index) => (
        <SyncCard key={`${sync.source}-${index}`} status={sync.status}>
          <StatusIcon status={sync.status} />
          <SyncInfo>
            <SyncTitle>
              {sync.status === 'syncing' ? 'Syncing' : 
               sync.status === 'synced' ? 'Synced' :
               sync.status === 'error' ? 'Sync Failed' : 'Offline'}
            </SyncTitle>
            <SyncDetails>
              {sync.status === 'syncing' && sync.progress !== undefined && (
                <>Updating {sync.source} data... {sync.progress}%</>
              )}
              {sync.status === 'synced' && sync.recordsUpdated && (
                <>{sync.recordsUpdated.toLocaleString()} records updated</>
              )}
              {sync.status === 'error' && (
                <>Failed to sync {sync.source}</>
              )}
              {sync.message && <div>{sync.message}</div>}
            </SyncDetails>
            {sync.progress !== undefined && (
              <ProgressBar>
                <ProgressFill progress={sync.progress} />
              </ProgressBar>
            )}
            <DataSourceIndicator>
              <SourceIcon color={getSourceColor(sync.source)} />
              <SourceName>{sync.source}</SourceName>
              {sync.lastSync && sync.status === 'synced' && (
                <Timestamp>• {formatTime(sync.lastSync)}</Timestamp>
              )}
            </DataSourceIndicator>
          </SyncInfo>
        </SyncCard>
      ))}
    </Container>
  );
};

// Inline sync status component for data source cards
const InlineContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
`;

const InlineStatus = styled.div<{ status: 'active' | 'syncing' | 'error' | 'offline' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  background: ${props => {
    switch(props.status) {
      case 'active': return Colors.success + '10';
      case 'syncing': return Colors.info + '10';
      case 'error': return Colors.danger + '10';
      case 'offline': return Colors.warning + '10';
      default: return Colors.surfaceHover;
    }
  }};
  border-radius: ${BorderRadius.xs}px;
  border: 1px solid ${props => {
    switch(props.status) {
      case 'active': return Colors.success + '30';
      case 'syncing': return Colors.info + '30';
      case 'error': return Colors.danger + '30';
      case 'offline': return Colors.warning + '30';
      default: return Colors.border;
    }
  }};
`;

const InlineIcon = styled.div<{ status: 'active' | 'syncing' | 'error' | 'offline' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    switch(props.status) {
      case 'active': return Colors.success;
      case 'syncing': return Colors.info;
      case 'error': return Colors.danger;
      case 'offline': return Colors.warning;
      default: return Colors.textTertiary;
    }
  }};
  
  ${props => props.status === 'syncing' && css`
    animation: ${pulse} 2s infinite;
  `}
`;

const InlineText = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  font-weight: 500;
`;

interface InlineSyncStatusProps {
  status: 'active' | 'syncing' | 'error' | 'offline';
  lastSync?: Date;
}

export const InlineSyncStatus: React.FC<InlineSyncStatusProps> = ({ status, lastSync }) => {
  const getStatusText = () => {
    switch(status) {
      case 'active': return lastSync ? `Synced ${formatTime(lastSync)}` : 'Active';
      case 'syncing': return 'Syncing...';
      case 'error': return 'Sync failed';
      case 'offline': return 'Offline';
      default: return 'Unknown';
    }
  };

  const formatTime = (date: Date): string => {
    const now = new Date();
    const diff = Math.abs(now.getTime() - date.getTime());
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <InlineContainer>
      <InlineStatus status={status}>
        <InlineIcon status={status} />
        <InlineText>{getStatusText()}</InlineText>
      </InlineStatus>
    </InlineContainer>
  );
};

export default RealTimeSyncIndicator;